import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Languages, RefreshCw, ArrowRight, Copy, Settings, Sparkles, 
  Globe, CheckCircle2, AlertCircle, Package, Search, Filter
} from "lucide-react";

const API = "/api";

export default function TranslationPage() {
  const { token } = useAuth();
  const [languages, setLanguages] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tekli çeviri
  const [singleTranslation, setSingleTranslation] = useState({
    text: "",
    source_lang: "tr",
    target_lang: "en",
    result: ""
  });

  // Toplu çeviri
  const [bulkTranslation, setBulkTranslation] = useState({
    texts: {},
    target_lang: "en",
    results: {}
  });

  const [bulkInput, setBulkInput] = useState("");

  // Ürün çevirisi state
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productTranslating, setProductTranslating] = useState(false);
  const [translatedProducts, setTranslatedProducts] = useState([]);
  const [productTargetLang, setProductTargetLang] = useState("en");
  const [translateOptions, setTranslateOptions] = useState({
    translate_names: true,
    translate_colors: true,
    translate_categories: true,
    translate_features: true,
    translate_descriptions: false
  });
  const [productSearch, setProductSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [langRes, settingsRes, productsRes] = await Promise.all([
        axios.get(`${API}/translation/languages?token=${token}`),
        axios.get(`${API}/translation/settings?token=${token}`),
        axios.get(`${API}/products?token=${token}&limit=100`)
      ]);
      setLanguages(langRes.data.languages);
      setSettings(settingsRes.data);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const translateSingle = async () => {
    if (!singleTranslation.text.trim()) {
      toast.error("Çevrilecek metin girin");
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translation/translate?token=${token}`, {
        text: singleTranslation.text,
        source_lang: singleTranslation.source_lang,
        target_lang: singleTranslation.target_lang
      });

      if (response.data.success) {
        setSingleTranslation(prev => ({
          ...prev,
          result: response.data.translated
        }));
        toast.success("Çeviri tamamlandı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Çeviri başarısız");
    } finally {
      setTranslating(false);
    }
  };

  const translateBulk = async () => {
    if (!bulkInput.trim()) {
      toast.error("Çevrilecek metinler girin");
      return;
    }

    // Input'u parse et (her satır bir key:value)
    const lines = bulkInput.trim().split("\n");
    const texts = {};
    lines.forEach((line, i) => {
      if (line.trim()) {
        const parts = line.split(":");
        if (parts.length >= 2) {
          texts[parts[0].trim()] = parts.slice(1).join(":").trim();
        } else {
          texts[`text_${i+1}`] = line.trim();
        }
      }
    });

    if (Object.keys(texts).length === 0) {
      toast.error("Geçerli metin bulunamadı");
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translation/bulk?token=${token}`, {
        texts,
        target_lang: bulkTranslation.target_lang
      });

      if (response.data.success) {
        setBulkTranslation(prev => ({
          ...prev,
          texts,
          results: response.data.translations
        }));
        toast.success(`${response.data.count} metin çevrildi`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Toplu çeviri başarısız");
    } finally {
      setTranslating(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/translation/settings?token=${token}`, settings);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Panoya kopyalandı");
  };

  // Ürün çevirisi fonksiyonları
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    const filteredProducts = products.filter(p => 
      p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.product_code?.toLowerCase().includes(productSearch.toLowerCase())
    );
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const translateSelectedProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Lütfen çevrilecek ürünleri seçin");
      return;
    }

    setProductTranslating(true);
    try {
      // Seçili ürünleri hazırla
      const productsToTranslate = products
        .filter(p => selectedProducts.includes(p.id))
        .map(p => ({
          product_code: p.product_code,
          name: p.product_name,
          color: p.color || "",
          category: p.main_category || "",
          features: p.features || [],
          description: p.description || ""
        }));

      const response = await axios.post(`${API}/translation/products?token=${token}`, {
        products: productsToTranslate,
        target_lang: productTargetLang,
        ...translateOptions
      });

      if (response.data.success) {
        setTranslatedProducts(response.data.translated_products);
        toast.success(`${response.data.translated_products.length} ürün çevrildi`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ürün çevirisi başarısız");
    } finally {
      setProductTranslating(false);
    }
  };

  const filteredProducts = products.filter(p => 
    p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.product_code?.toLowerCase().includes(productSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="translation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dil & Çeviri</h1>
          <p className="text-slate-500 mt-1">Gemini AI ile çoklu dil çeviri yönetimi</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700">
          <Sparkles className="w-4 h-4 mr-1" />
          Gemini AI
        </Badge>
      </div>

      {/* Desteklenen Diller */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Desteklenen Diller
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(languages).map(([code, name]) => (
              <Badge key={code} variant="outline" className="text-sm py-1 px-3">
                <span className="font-mono mr-2 text-slate-400">{code.toUpperCase()}</span>
                {name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="products" className="space-y-4">
        <TabsList>
          <TabsTrigger value="products">Ürün Çevirisi</TabsTrigger>
          <TabsTrigger value="single">Tekli Çeviri</TabsTrigger>
          <TabsTrigger value="bulk">Toplu Çeviri</TabsTrigger>
          <TabsTrigger value="settings">Ayarlar</TabsTrigger>
        </TabsList>

        {/* Ürün Çevirisi */}
        <TabsContent value="products">
          <div className="space-y-4">
            {/* Çeviri Ayarları */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Ürün Verisi Çevirisi
                </CardTitle>
                <CardDescription>
                  Ürün adları, renkler, kategoriler ve özelliklerini toplu olarak çevirin
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="translate_names"
                      checked={translateOptions.translate_names}
                      onCheckedChange={(c) => setTranslateOptions(prev => ({...prev, translate_names: c}))}
                    />
                    <Label htmlFor="translate_names">Ürün Adları</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="translate_colors"
                      checked={translateOptions.translate_colors}
                      onCheckedChange={(c) => setTranslateOptions(prev => ({...prev, translate_colors: c}))}
                    />
                    <Label htmlFor="translate_colors">Renkler</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="translate_categories"
                      checked={translateOptions.translate_categories}
                      onCheckedChange={(c) => setTranslateOptions(prev => ({...prev, translate_categories: c}))}
                    />
                    <Label htmlFor="translate_categories">Kategoriler</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="translate_features"
                      checked={translateOptions.translate_features}
                      onCheckedChange={(c) => setTranslateOptions(prev => ({...prev, translate_features: c}))}
                    />
                    <Label htmlFor="translate_features">Özellikler</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="translate_descriptions"
                      checked={translateOptions.translate_descriptions}
                      onCheckedChange={(c) => setTranslateOptions(prev => ({...prev, translate_descriptions: c}))}
                    />
                    <Label htmlFor="translate_descriptions">Açıklamalar</Label>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>Hedef Dil:</Label>
                    <Select value={productTargetLang} onValueChange={setProductTargetLang}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(languages).map(([code, name]) => (
                          <SelectItem key={code} value={code}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Ürün ara..."
                      className="pl-10"
                    />
                  </div>
                  <Button 
                    onClick={translateSelectedProducts}
                    disabled={productTranslating || selectedProducts.length === 0}
                  >
                    {productTranslating ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Languages className="w-4 h-4 mr-2" />
                    )}
                    Seçilenleri Çevir ({selectedProducts.length})
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ürün Listesi */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Ürünler</CardTitle>
                  <Button variant="outline" size="sm" onClick={selectAllProducts}>
                    {selectedProducts.length === filteredProducts.length ? "Tümünü Kaldır" : "Tümünü Seç"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Ürün Kodu</TableHead>
                        <TableHead>Ürün Adı</TableHead>
                        <TableHead>Renk</TableHead>
                        <TableHead>Kategori</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.slice(0, 20).map((product) => (
                        <TableRow key={product.id} className="cursor-pointer hover:bg-slate-50">
                          <TableCell>
                            <Checkbox 
                              checked={selectedProducts.includes(product.id)}
                              onCheckedChange={() => toggleProductSelection(product.id)}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{product.product_code}</TableCell>
                          <TableCell>{product.product_name}</TableCell>
                          <TableCell>{product.color || "-"}</TableCell>
                          <TableCell>{product.main_category || "-"}</TableCell>
                        </TableRow>
                      ))}
                      {filteredProducts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            Ürün bulunamadı
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {filteredProducts.length > 20 && (
                  <p className="text-sm text-slate-500 mt-2 text-center">
                    {filteredProducts.length} üründen ilk 20 tanesi gösteriliyor
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Çeviri Sonuçları */}
            {translatedProducts.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    Çeviri Sonuçları ({translatedProducts.length} ürün)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün Kodu</TableHead>
                          <TableHead>Orijinal Ad</TableHead>
                          <TableHead>Çeviri (Ad)</TableHead>
                          <TableHead>Çeviri (Renk)</TableHead>
                          <TableHead>Çeviri (Kategori)</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {translatedProducts.map((tp) => {
                          const original = products.find(p => p.product_code === tp.product_code);
                          return (
                            <TableRow key={tp.product_code}>
                              <TableCell className="font-mono text-sm">{tp.product_code}</TableCell>
                              <TableCell className="text-slate-500">{original?.product_name}</TableCell>
                              <TableCell className="font-medium">{tp.name || "-"}</TableCell>
                              <TableCell>{tp.color || "-"}</TableCell>
                              <TableCell>{tp.category || "-"}</TableCell>
                              <TableCell>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => copyToClipboard(JSON.stringify(tp, null, 2))}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button onClick={() => {
                      const data = JSON.stringify(translatedProducts, null, 2);
                      copyToClipboard(data);
                    }}>
                      <Copy className="w-4 h-4 mr-2" />
                      Tüm Sonuçları Kopyala (JSON)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Tekli Çeviri */}
        <TabsContent value="single">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kaynak */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Kaynak Metin</CardTitle>
                  <Select 
                    value={singleTranslation.source_lang}
                    onValueChange={(v) => setSingleTranslation(prev => ({...prev, source_lang: v}))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Otomatik Algıla</SelectItem>
                      {Object.entries(languages).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea 
                  value={singleTranslation.text}
                  onChange={(e) => setSingleTranslation(prev => ({...prev, text: e.target.value}))}
                  placeholder="Çevrilecek metni girin..."
                  className="min-h-[200px]"
                />
              </CardContent>
            </Card>

            {/* Hedef */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Çeviri</CardTitle>
                  <Select 
                    value={singleTranslation.target_lang}
                    onValueChange={(v) => setSingleTranslation(prev => ({...prev, target_lang: v}))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(languages).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Textarea 
                    value={singleTranslation.result}
                    readOnly
                    placeholder="Çeviri burada görünecek..."
                    className="min-h-[200px] bg-slate-50"
                  />
                  {singleTranslation.result && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(singleTranslation.result)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-center mt-4">
            <Button 
              size="lg" 
              onClick={translateSingle}
              disabled={translating || !singleTranslation.text.trim()}
              className="px-8"
            >
              {translating ? (
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Languages className="w-5 h-5 mr-2" />
              )}
              Çevir
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </TabsContent>

        {/* Toplu Çeviri */}
        <TabsContent value="bulk">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Metinler (Her satır bir çeviri)</CardTitle>
                <CardDescription>
                  Format: key: metin veya sadece metin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea 
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  placeholder={`title: Ürün Başlığı\ndescription: Ürün açıklaması\nbutton_text: Satın Al`}
                  className="min-h-[300px] font-mono text-sm"
                />
              </CardContent>
            </Card>

            {/* Output */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Çeviriler</CardTitle>
                  <Select 
                    value={bulkTranslation.target_lang}
                    onValueChange={(v) => setBulkTranslation(prev => ({...prev, target_lang: v}))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(languages).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 min-h-[300px]">
                  {Object.keys(bulkTranslation.results).length > 0 ? (
                    Object.entries(bulkTranslation.results).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
                        <span className="font-mono text-xs text-slate-500 mt-1 min-w-[80px]">{key}:</span>
                        <span className="flex-1 text-sm">{value}</span>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(value)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      Çeviriler burada görünecek
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-center mt-4">
            <Button 
              size="lg" 
              onClick={translateBulk}
              disabled={translating || !bulkInput.trim()}
              className="px-8"
            >
              {translating ? (
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Languages className="w-5 h-5 mr-2" />
              )}
              Toplu Çevir
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </TabsContent>

        {/* Ayarlar */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Çeviri Ayarları
              </CardTitle>
              <CardDescription>
                Otomatik çeviri ve varsayılan dil ayarlarını yapılandırın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Çeviri Modülü</Label>
                  <p className="text-sm text-slate-500">Çeviri özelliklerini etkinleştir</p>
                </div>
                <Switch 
                  checked={settings?.enabled ?? true}
                  onCheckedChange={(checked) => setSettings(prev => ({...prev, enabled: checked}))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Varsayılan Kaynak Dil</Label>
                  <Select 
                    value={settings?.defaultSourceLang || "tr"}
                    onValueChange={(v) => setSettings(prev => ({...prev, defaultSourceLang: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(languages).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Varsayılan Hedef Dil</Label>
                  <Select 
                    value={settings?.defaultTargetLang || "en"}
                    onValueChange={(v) => setSettings(prev => ({...prev, defaultTargetLang: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(languages).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h4 className="font-medium">Otomatik Çeviri</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Ürün Başlıklarını Otomatik Çevir</Label>
                    <p className="text-sm text-slate-500">Yeni ürünler eklendiğinde otomatik çevir</p>
                  </div>
                  <Switch 
                    checked={settings?.autoTranslateProducts ?? false}
                    onCheckedChange={(checked) => setSettings(prev => ({...prev, autoTranslateProducts: checked}))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Açıklamaları Otomatik Çevir</Label>
                    <p className="text-sm text-slate-500">Ürün açıklamalarını otomatik çevir</p>
                  </div>
                  <Switch 
                    checked={settings?.autoTranslateDescriptions ?? false}
                    onCheckedChange={(checked) => setSettings(prev => ({...prev, autoTranslateDescriptions: checked}))}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveSettings} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Kaydet
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Info */}
          <Card className="mt-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-purple-900">Gemini AI ile Çeviri</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    Çeviriler Google'ın Gemini 3 Flash modeli ile yapılmaktadır. 
                    E-ticaret terminolojisine özel optimize edilmiştir.
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-sm text-purple-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>10 dil desteği</span>
                    <span className="mx-2">•</span>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Toplu çeviri</span>
                    <span className="mx-2">•</span>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Teknik terim koruma</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
